import React, { useEffect, useState } from 'react';
import { exportDataset } from '../../lib/callables';

export default function Export() {
  const [versionTag, setVersionTag] = useState('');
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; exported: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Export — Pixano';
  }, []);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      setResult(null);
      
      const exportResult = await exportDataset(versionTag || undefined);
      setResult(exportResult);
    } catch (err) {
      console.error('Export failed:', err);
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const defaultVersionTag = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  return (
    <div className="max-w-2xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Dataset Export</h1>
      
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Version Tag (optional)
          </label>
          <input
            type="text"
            value={versionTag}
            onChange={(e) => setVersionTag(e.target.value)}
            placeholder={defaultVersionTag}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            Leave empty to use today's date: {defaultVersionTag}
          </p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Export Information</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Exports all approved dataset items to Google Cloud Storage</li>
            <li>• Creates COCO format JSON files</li>
            <li>• Creates YOLO format text files</li>
            <li>• Marks items as exported with timestamp</li>
            <li>• Storage location: gs://pixano-exports/{versionTag || defaultVersionTag}/</li>
          </ul>
        </div>
        
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? 'Exporting...' : 'Start Export'}
        </button>
        
        {result && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <h4 className="text-green-800 font-medium">Export Completed</h4>
            <p className="text-green-700">
              Successfully exported {result.exported} items
            </p>
          </div>
        )}
        
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <h4 className="text-red-800 font-medium">Export Failed</h4>
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>
      
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-medium mb-2">Scheduled Exports</h3>
        <p className="text-sm text-gray-600">
          Automatic exports run daily at 2:00 AM UTC via Cloud Scheduler.
          Manual exports can be triggered here for immediate processing.
        </p>
      </div>
    </div>
  );
}
